import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';

function CrearPost() {
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showFormatGuide, setShowFormatGuide] = useState(true);
  const [activeTab, setActiveTab] = useState('editor');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  
  const [postData, setPostData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?auto=format&fit=crop&q=80',
    category: 'arquitectura',
    is_published: true,
    tags: '',
    reading_time: 5
  });

  // Calcular estadísticas y tiempo de lectura
  useEffect(() => {
    const text = postData.content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
    setCharCount(text.length);
    
    // Calcular tiempo de lectura (250 palabras por minuto)
    const readingTime = Math.max(1, Math.ceil(words.length / 250));
    setPostData(prev => ({ ...prev, reading_time: readingTime }));
  }, [postData.content]);

  // Funciones de formateo mejoradas
  const insertFormat = (format, value = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = postData.content.substring(start, end);
    let newText = '';
    let cursorOffset = 0;

    switch (format) {
      case 'h1':
        newText = `<h1 class="text-4xl font-serif italic font-bold mt-8 mb-6 text-slate-900">${selectedText || 'Título Principal'}</h1>`;
        cursorOffset = selectedText ? 0 : -14;
        break;
      case 'h2':
        newText = `<h2 class="text-2xl font-serif font-semibold mt-6 mb-4 text-slate-800">${selectedText || 'Subtítulo'}</h2>`;
        cursorOffset = selectedText ? 0 : -11;
        break;
      case 'h3':
        newText = `<h3 class="text-xl font-serif font-medium mt-5 mb-3 text-slate-700">${selectedText || 'Subtítulo Pequeño'}</h3>`;
        cursorOffset = selectedText ? 0 : -18;
        break;
      case 'bold':
        newText = `<strong class="font-bold text-slate-900">${selectedText || 'texto en negrita'}</strong>`;
        cursorOffset = selectedText ? 0 : -21;
        break;
      case 'italic':
        newText = `<em class="italic text-slate-700">${selectedText || 'texto en cursiva'}</em>`;
        cursorOffset = selectedText ? 0 : -20;
        break;
      case 'quote':
        newText = `<blockquote class="border-l-4 border-blue-500 pl-6 py-2 my-4 italic text-lg text-slate-600 bg-blue-50 rounded-r-xl">${selectedText || 'Cita inspiradora...'}</blockquote>`;
        cursorOffset = selectedText ? 0 : -25;
        break;
      case 'ul':
        newText = `<ul class="list-disc pl-6 my-4 space-y-2 text-slate-700">\n  <li>Elemento de lista</li>\n  <li>Otro elemento</li>\n</ul>`;
        cursorOffset = -45;
        break;
      case 'ol':
        newText = `<ol class="list-decimal pl-6 my-4 space-y-2 text-slate-700">\n  <li>Primer elemento</li>\n  <li>Segundo elemento</li>\n</ol>`;
        cursorOffset = -49;
        break;
      case 'code':
        newText = `<pre class="bg-slate-900 text-slate-100 p-4 rounded-lg my-4 overflow-x-auto font-mono text-sm"><code>${selectedText || '// Tu código aquí'}</code></pre>`;
        cursorOffset = selectedText ? 0 : -15;
        break;
      case 'image':
        const imgUrl = window.prompt('URL de la imagen:', 'https://images.unsplash.com/photo-...');
        if (!imgUrl) return;
        const altText = window.prompt('Texto alternativo:', 'Descripción de la imagen');
        newText = `<div class="my-8">\n  <img src="${imgUrl}" alt="${altText}" class="rounded-2xl shadow-lg w-full h-auto" />\n  <p class="text-center text-sm text-slate-500 mt-2">${altText}</p>\n</div>`;
        cursorOffset = 0;
        break;
      case 'link':
        const url = window.prompt('URL del enlace:', 'https://ejemplo.com');
        if (!url) return;
        const linkText = window.prompt('Texto del enlace:', selectedText || 'Enlace de ejemplo');
        newText = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors">${linkText}</a>`;
        cursorOffset = 0;
        break;
      case 'highlight':
        newText = `<mark class="bg-yellow-100 px-1 py-0.5 rounded">${selectedText || 'texto resaltado'}</mark>`;
        cursorOffset = selectedText ? 0 : -16;
        break;
      case 'divider':
        newText = '<div class="my-8 py-4 border-t border-b border-slate-200">\n  <div class="text-center text-slate-400 italic">📝 Punto de interés</div>\n</div>';
        cursorOffset = -50;
        break;
      default:
        return;
    }

    const updatedContent = 
      postData.content.substring(0, start) + 
      newText + 
      postData.content.substring(end);
    
    setPostData(prev => ({ ...prev, content: updatedContent }));

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + newText.length + cursorOffset;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const formatButtons = [
    { icon: 'H1', title: 'Título principal', action: 'h1' },
    { icon: 'H2', title: 'Subtítulo', action: 'h2' },
    { icon: 'H3', title: 'Subtítulo pequeño', action: 'h3' },
    { icon: '𝐁', title: 'Negrita', action: 'bold' },
    { icon: '𝐼', title: 'Cursiva', action: 'italic' },
    { icon: '💬', title: 'Cita', action: 'quote' },
    { icon: '📝', title: 'Resaltar', action: 'highlight' },
    { icon: '🔗', title: 'Enlace', action: 'link' },
    { icon: '🖼️', title: 'Imagen', action: 'image' },
    { icon: '•', title: 'Lista con viñetas', action: 'ul' },
    { icon: '1.', title: 'Lista numerada', action: 'ol' },
    { icon: '</>', title: 'Código', action: 'code' },
    { icon: '⸻', title: 'Divisor', action: 'divider' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!postData.title.trim() || !postData.content.trim()) {
      setError('El título y contenido son obligatorios');
      setLoading(false);
      return;
    }

    try {
      const slug = generateSlug(postData.title);
      const postToSend = {
        ...postData,
        slug,
        tags: postData.tags ? postData.tags.split(',').map(tag => tag.trim()) : []
      };

      const response = await api.post('/posts', postToSend);
      
      setSuccess('🎉 ¡Artículo creado exitosamente!');
      
      setTimeout(() => {
        navigate(`/blog/${response.data.slug}`);
      }, 2000);
      
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al crear el artículo');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  };

  const imageOptions = [
    'https://images.unsplash.com/photo-1600566752355-35792bedcfea?auto=format&fit=crop&w=1200',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200',
    'https://images.unsplash.com/photo-1600607687644-c7171b42498b?auto=format&fit=crop&w=1200',
    'https://images.unsplash.com/photo-1600566753190-17a0ce06de74?auto=format&fit=crop&w=1200',
    'https://images.unsplash.com/photo-1513584684374-8bab748fbf90?auto=format&fit=crop&w=1200',
    'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200'
  ];

  const categories = [
    { value: 'arquitectura', label: '🏛️ Arquitectura', color: 'bg-purple-100 text-purple-800' },
    { value: 'diseno', label: '🎨 Diseño', color: 'bg-pink-100 text-pink-800' },
    { value: 'inversion', label: '💰 Inversión', color: 'bg-green-100 text-green-800' },
    { value: 'tendencias', label: '📈 Tendencias', color: 'bg-blue-100 text-blue-800' },
    { value: 'lifestyle', label: '🌿 Lifestyle', color: 'bg-teal-100 text-teal-800' },
    { value: 'mercado', label: '📊 Mercado', color: 'bg-amber-100 text-amber-800' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors group mb-3"
              >
                <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="text-sm font-medium">Volver al Blog</span>
              </Link>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-2">
                Nueva Publicación
              </h1>
              <p className="text-slate-500">
                Crea contenido memorable para la comunidad VEstates
              </p>
            </div>
            <div className="hidden md:block">
              <div className="flex items-center gap-3 text-sm text-slate-600 bg-white px-4 py-2 rounded-full border border-slate-200">
                <div className="flex items-center gap-1">
                  <span className="text-blue-600">📝</span>
                  <span>{wordCount} palabras</span>
                </div>
                <div className="h-4 w-px bg-slate-300"></div>
                <div className="flex items-center gap-1">
                  <span className="text-blue-600">⏱️</span>
                  <span>{postData.reading_time} min lectura</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensajes */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 animate-in fade-in">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl text-green-600 animate-in fade-in">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar izquierdo - Metadatos */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Configuración</h3>
              
              <div className="space-y-4">
                {/* Imagen destacada */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Imagen destacada
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {imageOptions.map((imgUrl, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setPostData({...postData, image: imgUrl})}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          postData.image === imgUrl 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <img 
                          src={imgUrl} 
                          alt={`Opción ${idx + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </button>
                    ))}
                  </div>
                  <input
                    type="url"
                    value={postData.image}
                    onChange={(e) => setPostData({...postData, image: e.target.value})}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Categoría */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Categoría
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map(cat => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setPostData({...postData, category: cat.value})}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          postData.category === cat.value 
                            ? `${cat.color} ring-2 ring-offset-1 ring-current/20` 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Etiquetas */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Etiquetas
                  </label>
                  <input
                    type="text"
                    value={postData.tags}
                    onChange={(e) => setPostData({...postData, tags: e.target.value})}
                    placeholder="arquitectura, diseño, inversión"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Estado de publicación */}
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={postData.is_published}
                          onChange={(e) => setPostData({...postData, is_published: e.target.checked})}
                          className="sr-only"
                        />
                        <div className={`w-11 h-6 rounded-full transition ${postData.is_published ? 'bg-blue-600' : 'bg-slate-300'}`}>
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${postData.is_published ? 'transform translate-x-6' : 'transform translate-x-1'}`} />
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700">
                          {postData.is_published ? 'Publicado' : 'Borrador'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {postData.is_published ? 'Visible públicamente' : 'Solo tú puedes verlo'}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Estadísticas</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Palabras</span>
                  <span className="font-semibold text-slate-900">{wordCount}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Caracteres</span>
                  <span className="font-semibold text-slate-900">{charCount}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Tiempo lectura</span>
                  <span className="font-semibold text-slate-900">{postData.reading_time} min</span>
                </div>
              </div>
            </div>

            {/* Botón de publicación */}
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !postData.title || !postData.content}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                  Publicando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {postData.is_published ? 'Publicar artículo' : 'Guardar borrador'}
                </>
              )}
            </button>
          </div>

          {/* Área principal del editor */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              {/* Toolbar */}
              <div className="border-b border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-1 mb-3">
                  {formatButtons.map((btn, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => insertFormat(btn.action)}
                      className="px-3 py-2 rounded-lg text-sm font-medium transition-all hover:bg-slate-100 text-slate-700 hover:text-slate-900 flex items-center gap-2"
                      title={btn.title}
                    >
                      <span className="text-lg">{btn.icon}</span>
                      <span className="hidden md:inline text-xs">{btn.title}</span>
                    </button>
                  ))}
                  
                  <button
                    type="button"
                    onClick={() => setShowFormatGuide(!showFormatGuide)}
                    className="ml-auto px-3 py-2 text-xs text-slate-500 hover:text-slate-700"
                  >
                    {showFormatGuide ? '👁️ Ocultar ayuda' : '👁️ Mostrar ayuda'}
                  </button>
                </div>

                {/* Guía de formato */}
                {showFormatGuide && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-200 px-1.5 py-0.5 rounded"># Título</code>
                        <span>→ H1</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-200 px-1.5 py-0.5 rounded">## Subtítulo</code>
                        <span>→ H2</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-200 px-1.5 py-0.5 rounded">**negrita**</code>
                        <span>→ <strong>Negrita</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-200 px-1.5 py-0.5 rounded">*cursiva*</code>
                        <span>→ <em>Cursiva</em></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Título */}
              <div className="p-6 border-b border-slate-100">
                <input
                  type="text"
                  value={postData.title}
                  onChange={(e) => setPostData({...postData, title: e.target.value})}
                  placeholder="Escribe el título de tu artículo aquí..."
                  className="w-full text-2xl md:text-3xl font-serif italic font-bold text-slate-900 placeholder-slate-400 focus:outline-none"
                  required
                />
                <div className="mt-2 text-sm text-slate-500">
                  Slug generado: <code className="bg-slate-100 px-2 py-1 rounded text-xs">{generateSlug(postData.title) || 'slug-del-articulo'}</code>
                </div>
              </div>

              {/* Extracto */}
              <div className="p-6 border-b border-slate-100">
                <textarea
                  value={postData.excerpt}
                  onChange={(e) => setPostData({...postData, excerpt: e.target.value})}
                  placeholder="Escribe un extracto atractivo para redes sociales (140-160 caracteres ideal para SEO)..."
                  rows="2"
                  className="w-full text-slate-700 placeholder-slate-400 focus:outline-none resize-none text-lg"
                />
                <div className="mt-2 flex justify-between text-xs text-slate-500">
                  <span>{postData.excerpt.length}/160 caracteres</span>
                  <span className={postData.excerpt.length >= 140 && postData.excerpt.length <= 160 ? 'text-green-600' : 'text-amber-600'}>
                    {postData.excerpt.length >= 140 && postData.excerpt.length <= 160 ? '✅ Ideal para SEO' : '⚠️ Optimiza para SEO'}
                  </span>
                </div>
              </div>

              {/* Pestañas Editor/Vista Previa */}
              <div className="border-b border-slate-200">
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => setActiveTab('editor')}
                    className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'editor' 
                        ? 'text-blue-600 border-b-2 border-blue-600' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    ✍️ Editor
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('preview')}
                    className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'preview' 
                        ? 'text-blue-600 border-b-2 border-blue-600' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    👁️ Vista Previa
                  </button>
                </div>
              </div>

              {/* Área de contenido */}
              <div className="min-h-[500px]">
                {activeTab === 'editor' ? (
                  <textarea
                    ref={textareaRef}
                    value={postData.content}
                    onChange={(e) => setPostData({...postData, content: e.target.value})}
                    placeholder="Comienza a escribir tu artículo... Usa los botones de arriba para formatear o escribe Markdown (# para títulos, **para negrita**, *para cursiva*)"
                    className="w-full h-[500px] p-6 text-slate-700 focus:outline-none resize-none font-mono text-sm leading-relaxed bg-white"
                    spellCheck="true"
                  />
                ) : (
                  <div className="p-6 h-[500px] overflow-y-auto">
                    {postData.content ? (
                      <div 
                        className="prose prose-lg max-w-none"
                        dangerouslySetInnerHTML={{ __html: postData.content }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <div className="text-5xl mb-4">📝</div>
                        <p className="text-lg font-medium">Aún no hay contenido</p>
                        <p className="text-sm mt-2">Comienza a escribir en la pestaña "Editor"</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Barra de estado */}
              <div className="border-t border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <span>📄</span>
                      <span>{wordCount} palabras</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>🔤</span>
                      <span>{charCount} caracteres</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>⏱️</span>
                      <span>{postData.reading_time} min lectura</span>
                    </div>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setPostData({...postData, content: ''})}
                      className="text-slate-400 hover:text-red-600 transition-colors text-xs"
                    >
                      Limpiar todo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Nota al pie */}
            <div className="mt-4 text-xs text-slate-500 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>💡</span>
                <span>Usa Markdown o los botones para formatear. Se guarda automáticamente.</span>
              </div>
              <div>
                <span className="px-2 py-1 bg-slate-100 rounded">Autoguardado: Justo ahora</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CrearPost;